import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { CheckCircle, Save, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SupplementalEssaysForm = ({ applicationId, collegeId, collegeName, onSave }) => {
  const [prompts, setPrompts] = useState([]);
  const [essays, setEssays] = useState({});
  const [savedEssays, setSavedEssays] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    if (collegeId) {
      loadPromptsAndEssays();
    }
  }, [collegeId, applicationId]);

  const loadPromptsAndEssays = async () => {
    try {
      // Load prompts
      const promptsRes = await axios.get(
        `${BACKEND_URL}/api/supplemental/prompts/${collegeId}`,
        getAuthHeaders()
      );
      setPrompts(promptsRes.data.prompts || []);

      // Load saved essays
      if (applicationId) {
        const essaysRes = await axios.get(
          `${BACKEND_URL}/api/supplemental/essays/${applicationId}`,
          getAuthHeaders()
        );
        
        const essaysMap = {};
        const savedMap = {};
        essaysRes.data.forEach(essay => {
          essaysMap[essay.prompt_id] = essay.essay_text;
          savedMap[essay.prompt_id] = essay.essay_text;
        });
        setEssays(essaysMap);
        setSavedEssays(savedMap);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEssay = async (promptId) => {
    if (!essays[promptId]?.trim()) {
      toast({ title: 'Error', description: 'Essay cannot be empty', variant: 'destructive' });
      return;
    }

    setSaving(prev => ({ ...prev, [promptId]: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/supplemental/essays/${applicationId}`,
        {
          prompt_id: promptId,
          essay_text: essays[promptId]
        },
        getAuthHeaders()
      );
      
      setSavedEssays(prev => ({ ...prev, [promptId]: essays[promptId] }));
      toast({ title: 'Saved', description: 'Essay saved successfully' });
      
      if (onSave) onSave();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save essay',
        variant: 'destructive'
      });
    } finally {
      setSaving(prev => ({ ...prev, [promptId]: false }));
    }
  };

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const isOverLimit = (text, limit) => getWordCount(text) > limit;
  const hasUnsavedChanges = (promptId) => essays[promptId] !== savedEssays[promptId];
  const isComplete = (promptId) => savedEssays[promptId]?.trim()?.length > 0;

  if (loading) {
    return <div className="text-center py-8">Loading essay prompts...</div>;
  }

  if (prompts.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No supplemental essays required for this college.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Supplemental Essays</h3>
          <p className="text-sm text-gray-600">{collegeName || 'College'} specific questions</p>
        </div>
        <Badge variant="outline">
          {Object.keys(savedEssays).filter(k => savedEssays[k]).length} / {prompts.length} complete
        </Badge>
      </div>

      {prompts.map((prompt, index) => {
        const wordCount = getWordCount(essays[prompt.id]);
        const overLimit = isOverLimit(essays[prompt.id], prompt.word_limit);
        const complete = isComplete(prompt.id);
        const unsaved = hasUnsavedChanges(prompt.id);

        return (
          <Card key={prompt.id} className={complete ? 'border-green-200' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    Essay {index + 1}
                    {prompt.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    {!prompt.required && <Badge variant="secondary" className="text-xs">Optional</Badge>}
                    {complete && <CheckCircle className="h-4 w-4 text-green-600" />}
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {prompt.prompt_text}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={essays[prompt.id] || ''}
                onChange={(e) => setEssays(prev => ({ ...prev, [prompt.id]: e.target.value }))}
                placeholder="Write your response here..."
                rows={8}
                className={overLimit ? 'border-red-500' : ''}
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className={`text-sm ${overLimit ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    {wordCount} / {prompt.word_limit} words
                  </span>
                  {overLimit && (
                    <span className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      Over word limit
                    </span>
                  )}
                  {unsaved && !overLimit && (
                    <span className="text-sm text-amber-600">Unsaved changes</span>
                  )}
                </div>
                <Button
                  onClick={() => handleSaveEssay(prompt.id)}
                  disabled={saving[prompt.id] || overLimit || !essays[prompt.id]?.trim()}
                  size="sm"
                >
                  {saving[prompt.id] ? (
                    'Saving...'
                  ) : (
                    <><Save className="h-4 w-4 mr-1" /> Save Essay</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SupplementalEssaysForm;
